import React, {useState, useEffect} from 'react';
import {graphql} from 'gatsby';
import Seo from "/src/components/Seo";
import Layout from "../layouts/sample";
import {useTranslation} from "gatsby-plugin-react-i18next";
import Link from "../components/global/Link";

const BlogTag = (props) => {
  const {t} = useTranslation();
  const tag = props.data?.tag || {};
  const translations = tag?.translations || {};
  const posts = tag?.posts?.nodes || [];
  const postsPerPage = 32;

  const [postsCount, setPostsCount] = useState(postsPerPage);
  const [list, setList] = useState(posts);
  const [hasMore, setHasMore] = useState(posts.length > postsCount);
  const [filter, setFilter] = useState("");
  const [squery, setSquery] = useState("");

  const getTitle = (title, query) => {
    if (!query) return title;
    return title.replace(new RegExp(query, "gi"), (match) => `<mark>${match}</mark>`);
  }

  const handleLoadMore = () => {
    setPostsCount(postsCount + postsPerPage);
  };

  const handleSearch = (event) => {
    setSquery(event.target.value);
    setPostsCount(postsPerPage);
  };

  const search = (items) => {
    return items.filter((item) => (!filter || (item?.categories?.nodes.length > 0 && item.categories.nodes.some(cat => cat.id === +filter))) && (item.title
      .toString()
      .toLowerCase()
      .indexOf(squery.toLowerCase()) > -1));
  };

  const ResetSearch = () => {
    setSquery('');
    setPostsCount(postsPerPage);
  };

  useEffect(() => {
    setList(search(posts));
  }, [posts, filter, squery, postsCount]);

  useEffect(() => {
    const isMore = list.length > postsCount;
    setHasMore(isMore);
  }, [list, postsCount]);

  const groupPostsByCategory = (posts) => {
    const grouped = {};
    posts.forEach(post => {
      post.categories.nodes.forEach(category => {
        if (!grouped[category.name]) {
          grouped[category.name] = [];
        }
        grouped[category.name].push(post);
      });
    });
    return grouped;
  };

  const groupedPosts = groupPostsByCategory(list);

  const LoadMoreButton = () => {
    if (hasMore) return (
      <div className="text-center">
        <button type="button" className="btn btn-outline-white btn-lg" onClick={handleLoadMore}>
          {t("Load More")}
        </button>
      </div>
    )
  };

  return (<Layout translations={translations}>
      <section className={`pt-14-rem pb-lg-0 overflow-hidden`}>
        <div className={`container`}>
          <h1 className="h3 mb-3 mb-md-4 mb-lg-5 mb-xxl-9">
            "{tag.name}" {t(`tag related articles`)}
            {posts.length > 0 && <span className={`zg-label bg-cyan ms-2 fs-14 align-top`}>{posts.length}</span>}
          </h1>
          <div className="pt-3 pb-2 pt-md-6 pb-md-4">
            <form action="#" className={`position-relative hs-form`} onSubmit={() => false} data-bs-theme="dark">
              <svg width="20" height="20" className="position-absolute top-50 translate-middle-y ms-3 opacity-75">
                <use xlinkHref="#icon-search"/>
              </svg>
              <input type="search" value={squery} placeholder={`Search`}
                     className={`hs-input form-control-alt form-control-alt-md px-7 pe-8`}
                     onChange={handleSearch}/>
              <button type={`button`}
                      className={`p-0 border-0 bg-transparent text-gray position-absolute top-50 translate-middle-y end-0 p-2 me-2 z-1 ${!squery && 'd-none'}`}
                      onClick={ResetSearch} aria-label="Close">
                <svg width="16" height="16" className="d-block">
                  <use xlinkHref="#icon-close"/>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </section>
      <section className="pt-3 pt-md-4 pb-10 pb-lg-14">
        <div className="container d-flex flex-column gap-4 gap-md-5">
          {Object.keys(groupedPosts).map((category, index) => (
            <div key={index}>
              <h2 className="fs-14 fw-normal mb-3 d-flex align-items-center gap-2 py-3 border-top border-gray border-bottom">
                <svg className="flex-shrink-0" width="16" height="16">
                  <use xlinkHref="#icon-rootstock"/>
                </svg>
                {category}
              </h2>
              <div className="d-flex flex-column gap-1 align-items-start">
                {groupedPosts[category].slice(0, postsCount).map((post, index) => (
                  <Link className="zg-link-white d-flex align-items-stretch gap-3 py-2 text-decoration-none" key={index} to={post.uri}>
                    <svg width={24} height={24} className="text-purple flex-shrink-0">
                      <use xlinkHref="#icon-doc"/>
                    </svg>
                    <span className="d-flex align-items-center" dangerouslySetInnerHTML={{__html:getTitle(post.title, squery)}}></span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="col-12 mx-auto">
              <p className={`text-center fs-18`}>
                {squery ? <>
                  {t('No results for query')}: <span className={`fw-bold`}>{squery}</span>
                </> : <>
                  {t('No results for tag')}: <span className={`fw-bold`}>{tag.name}</span>
                </>
                }
              </p>
            </div>
          )}
          <LoadMoreButton/>
        </div>
      </section>
    </Layout>);
};

export default BlogTag;

export const Head = (props) => {
  const seo = props?.data?.tag || {};
  const {t} = useTranslation();
  return <Seo title={`${seo.name} ${t('tag related articles')}`} description={seo?.description}/>;
};

export const pageQuery = graphql`
  query ($lng: String!, $id: Int!) {
    locales: allLocale(
      filter: { language: { eq: $lng } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    tag : wpTag(databaseId: {eq: $id}) {
      name
      translations {
        slug
      }
      posts{
        nodes {
          title
          uri
          date
          excerpt
          author {
            node {
              name
            }
          }
          categories {
            nodes {
              id : databaseId
              name
              slug
            }
          }
        }
      }
    }
  }
`;
